import React, { useState, useEffect } from 'react';
import { Clock, Trash2, Plus, Check, X, AlertTriangle } from 'lucide-react';
import { getLaneTypeColor } from './colorUtils';

interface TimeSlotCellProps {
  hours: any[];
  laneType: any;
  dayOfWeek: number;
  checkpointId: number;
  checkpointName: string;
  onUpdate: (hourId: number, startTime: string, endTime: string) => Promise<void>;
  onDelete: (hourId: number) => void;
  onAdd: (context: { checkpoint_id: number; checkpoint_name: string; day_of_week: number; lanetype_id: number }) => void;
  canUpdate?: boolean;
}

export const TimeSlotCell: React.FC<TimeSlotCellProps> = ({
  hours,
  laneType,
  dayOfWeek,
  checkpointId,
  checkpointName,
  onUpdate,
  onDelete,
  onAdd,
  canUpdate = false
}) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasOverlap, setHasOverlap] = useState(false);

  // Check for overlaps when editing
  useEffect(() => {
    if (editingId && editStartTime && editEndTime) {
      checkOverlap(editingId, editStartTime, editEndTime);
    } else {
      setHasOverlap(false);
    }
  }, [editStartTime, editEndTime, editingId]);

  const checkOverlap = (currentId: number, start: string, end: string) => {
    if (start >= end) {
      setHasOverlap(true);
      return;
    }

    // Check against other slots for the same lane type and day
    const otherSlots = hours.filter(h => h.lane_operating_hours_id !== currentId);
    
    const overlaps = otherSlots.some(slot => {
      const slotStart = formatTime(slot.start_time);
      const slotEnd = formatTime(slot.end_time);
      
      // Check if time ranges overlap
      return (
        (start >= slotStart && start < slotEnd) ||
        (end > slotStart && end <= slotEnd) ||
        (start <= slotStart && end >= slotEnd)
      );
    });

    setHasOverlap(overlaps);
  };

  const handleAddClick = () => {
    onAdd({
      checkpoint_id: checkpointId,
      checkpoint_name: checkpointName,
      day_of_week: dayOfWeek,
      lanetype_id: laneType.lane_type_id
    });
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const handleStartEdit = (hour: any) => {
    setEditingId(hour.lane_operating_hours_id);
    setEditStartTime(formatTime(hour.start_time));
    setEditEndTime(formatTime(hour.end_time));
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditStartTime('');
    setEditEndTime('');
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    // Client-side validation
    if (!editStartTime || !editEndTime) {
      alert('Please enter both start and end times');
      return;
    }

    if (editStartTime >= editEndTime) {
      alert('End time must be after start time');
      return;
    }

    if (hasOverlap) {
      alert('Time slot overlaps with existing hours for this lane type');
      return;
    }

    setSaving(true);
    try {
      await onUpdate(editingId, `${editStartTime}:00`, `${editEndTime}:00`);
      setEditingId(null);
      setEditStartTime('');
      setEditEndTime('');
    } catch (error) {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  if (hours.length === 0) {
    return (
      <td className="px-2 py-2 text-center">
        {canUpdate && (
          <button
            onClick={handleAddClick}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors"
          >
            <Plus size={14} />
            Add
          </button>
        )}
      </td>
    );
  }

  return (
    <td className="px-2 py-2">
      <div className="space-y-1.5">
        {hours.map((hour) => {
          const isEditing = editingId === hour.lane_operating_hours_id;
          const laneTypeColor = getLaneTypeColor(laneType.type_name);

          if (isEditing) {
            return (
              <div key={hour.lane_operating_hours_id} className="space-y-2">
                <div 
                  className={`flex items-center gap-2 p-2 ${laneTypeColor.bg} border ${laneTypeColor.border} rounded`}
                >
                  <input
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    className="w-20 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={saving}
                  />
                  <span className="text-gray-400 text-sm">-</span>
                  <input
                    type="time"
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                    className="w-20 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={saving}
                  />
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving || hasOverlap}
                    className={`p-1 rounded transition-colors ${
                      hasOverlap 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                    title={hasOverlap ? 'Overlaps with existing slot' : 'Save'}
                  >
                    <Check size={14} className="text-white" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="p-1 hover:bg-red-600 bg-red-500 rounded transition-colors disabled:opacity-50"
                    title="Cancel"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
                {hasOverlap && (
                  <div className="flex items-center gap-1 text-xs text-red-400 px-2">
                    <AlertTriangle size={12} />
                    <span>Overlaps with existing hours</span>
                  </div>
                )}
              </div>
            );
          }

          return (
            <div 
              key={hour.lane_operating_hours_id} 
              className={`group flex items-center justify-between gap-2 px-3 py-1.5 ${laneTypeColor.bg} border ${laneTypeColor.border} rounded ${canUpdate ? 'hover:opacity-80 cursor-pointer' : ''} transition-all`}
              onClick={canUpdate ? () => handleStartEdit(hour) : undefined}
            >
              <span className={`${laneTypeColor.text} text-sm font-mono font-medium flex items-center gap-1`}>
                <Clock size={12} className="opacity-60" />
                {formatTime(hour.start_time)} - {formatTime(hour.end_time)}
              </span>
              {canUpdate && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(hour.lane_operating_hours_id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-600 bg-red-500 rounded transition-all"
                  title="Delete"
                >
                  <Trash2 size={12} className="text-white" />
                </button>
              )}
            </div>
          );
        })}
        
        {/* Add button for additional slots */}
        {canUpdate && hours.length > 0 && (
          <button
            onClick={handleAddClick}
            className="w-full flex items-center justify-center gap-1 px-3 py-1 text-sm text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors"
          >
            <Plus size={14} />
            Add Slot
          </button>
        )}
      </div>
    </td>
  );
};
